import React, { useState, useEffect, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext.jsx";
import { useLanguage } from "../../contexts/LanguageContext";
import { ROLES, PERMISSIONS } from "../../utils/roles";
import './Employees.css';

// ============ TYPE DEFINITIONS ============
interface Employee {
  id: string;
  userId?: string;
  name: string;
  email?: string;
  phone: string;
  role: string;
  workShift: string;
  shiftStartTime?: string; // e.g. "07:00"
  shiftEndTime?: string;   // e.g. "12:00"
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
  joinedAt?: string;
  isFromUserSystem?: boolean;
}

interface EmployeeStats {
  total: number;
  active: number;
  byRole: {
    owner: number;
    manager: number;
    staff: number;
    receptionist: number;
  };
  byShift: {
    morning: number;
    afternoon: number;
    evening: number;
    night: number;
    fulltime: number;
    parttime: number;
  };
}

// ============ MAIN COMPONENT ============
const Employees: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterShift, setFilterShift] = useState<string>('all');
  const [stats, setStats] = useState<EmployeeStats | null>(null);
  const [shiftModalEmployee, setShiftModalEmployee] = useState<Employee | null>(null);
  const [shiftEdit, setShiftEdit] = useState({ workShift: 'Ca sáng', shiftStartTime: '07:00', shiftEndTime: '12:00' });

  const { user, hasPermission } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // Load danh sách nhân viên — dùng getBusinessMembers làm nguồn chính
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        setLoading(true);

        // 1. Lấy members từ User.businesses[] (nguồn chính)
        const membersRes = await apiService.getBusinessMembers();
        const membersList: any[] = membersRes?.data || [];

        // 2. Lấy Employee records (có thêm workShift, status chi tiết)
        let employeeRecords: any[] = [];
        try {
          const empRes = await apiService.getEmployees();
          employeeRecords = Array.isArray(empRes) ? empRes : (empRes?.data || []);
        } catch { /* Employee collection có thể rỗng */ }

        // 3. Merge: dùng member list, bổ sung workShift từ Employee record nếu có
        const merged: Employee[] = membersList.map((member: any) => {
          const empRecord = employeeRecords.find(
            (e: any) => e.userId?.toString() === member.userId?.toString()
          );
          return {
            id: empRecord?._id?.toString() || empRecord?.id?.toString() || member.userId?.toString() || member._id?.toString() || String(Date.now()),
            userId: member.userId?.toString(),
            name: member.name || member.username || 'N/A',
            email: member.email || '',
            phone: member.phone || empRecord?.phone || '',
            role: member.role || 'staff',
            workShift: empRecord?.workShift || 'Ca sáng',
            shiftStartTime: empRecord?.shiftStartTime || '07:00',
            shiftEndTime: empRecord?.shiftEndTime || '12:00',
            status: empRecord?.status || 'active',
            createdAt: member.joinedAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            joinedAt: member.joinedAt,
            isFromUserSystem: true,
          };
        });

        // 4. Thêm các Employee không có trong User.businesses (thêm thủ công cũ)
        const manualEmployees = employeeRecords
          .filter((e: any) => !e.userId || !membersList.find(
            (m: any) => m.userId?.toString() === e.userId?.toString()
          ))
          .map((e: any) => ({
            id: e._id || e.id || String(Date.now()),
            name: e.name,
            email: e.email || '',
            phone: e.phone || '',
            role: e.role || 'staff',
            workShift: e.workShift || 'Ca sáng',
            status: e.status || 'active',
            createdAt: e.createdAt || new Date().toISOString(),
            updatedAt: e.updatedAt || new Date().toISOString(),
            isFromUserSystem: false,
          }));

        setEmployees([...merged, ...manualEmployees]);
      } catch (error) {
        console.error('Error loading employees:', error);
        setEmployees([]);
      } finally {
        setLoading(false);
      }
    };

    loadEmployees();
  }, []);

  // Tải lại thống kê khi employees thay đổi
  useEffect(() => {
    const updateStats = async () => {
      try {
        if (employees.length > 0) {
          const localStats: EmployeeStats = {
            total: employees.length,
            active: employees.filter((e: Employee) => e.status === 'active').length,
            byRole: {
              owner: employees.filter((e: Employee) => e.role === ROLES.OWNER).length,
              manager: employees.filter((e: Employee) => e.role === ROLES.MANAGER).length,
              staff: employees.filter((e: Employee) => e.role === ROLES.STAFF).length,
              receptionist: employees.filter((e: Employee) => e.role === ROLES.RECEPTIONIST).length,
            },
            byShift: {
              morning: employees.filter((e: Employee) => e.workShift === 'Ca sáng').length,
              afternoon: employees.filter((e: Employee) => e.workShift === 'Ca chiều').length,
              evening: employees.filter((e: Employee) => e.workShift === 'Ca tối').length,
              night: employees.filter((e: Employee) => e.workShift === 'Ca đêm').length,
              fulltime: employees.filter((e: Employee) => e.workShift === 'Toàn thời gian').length,
              parttime: employees.filter((e: Employee) => e.workShift === 'Bán thời gian').length,
            }
          };
          setStats(localStats);
        } else {
          setStats(null);
        }
      } catch (error) {
        console.error('Error updating stats:', error);
        setStats(null);
      }
    };

    updateStats();
  }, [employees]);

  // Lọc và tìm kiếm nhân viên (client-side filtering)
  const filteredEmployees = employees.filter((employee: Employee) => {
    const matchesSearch =
      employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.phone.includes(searchTerm);

    const matchesRole = filterRole === 'all' || employee.role === filterRole;

    const matchesShift = filterShift === 'all' || employee.workShift === filterShift;

    return matchesSearch && matchesRole && matchesShift;
  });

  // Cập nhật nhân viên
  const handleUpdateEmployee = async (): Promise<void> => {
    if (!hasPermission(PERMISSIONS.MANAGE_EMPLOYEES)) {
      alert(t('employees.messages.noPermissionUpdate'));
      return;
    }

    if (!editingEmployee?.name.trim() || !editingEmployee?.phone.trim()) {
      alert(t('employees.messages.enterInfo'));
      return;
    }

    try {
      const { id, createdAt, updatedAt, isFromUserSystem, userId, joinedAt, ...updateData } = editingEmployee;
      const updatedEmployee = await apiService.updateEmployee(id, updateData);

      // Cập nhật state
      setEmployees(prev =>
        prev.map(emp => emp.id === id ? (updatedEmployee.data || updatedEmployee) : emp)
      );

      setEditingEmployee(null);
      alert(t('employees.messages.updateSuccess'));
    } catch (error: any) {
      console.error('Error updating employee:', error);
      alert(`${t('employees.messages.updateError')}: ${error.message}`);
    }
  };

  // ── Lưu ca làm việc với giờ tùy chỉnh ──────────────────────────────────────
  const SHIFT_PRESETS: Record<string, { start: string; end: string }> = {
    'Ca sáng':        { start: '07:00', end: '12:00' },
    'Ca chiều':       { start: '12:00', end: '17:00' },
    'Ca tối':         { start: '17:00', end: '22:00' },
    'Ca đêm':         { start: '22:00', end: '06:00' },
    'Toàn thời gian': { start: '08:00', end: '17:00' },
    'Bán thời gian':  { start: '09:00', end: '13:00' },
  };

  const openShiftModal = (emp: Employee) => {
    setShiftModalEmployee(emp);
    setShiftEdit({
      workShift:      emp.workShift || 'Ca sáng',
      shiftStartTime: emp.shiftStartTime || SHIFT_PRESETS[emp.workShift]?.start || '07:00',
      shiftEndTime:   emp.shiftEndTime   || SHIFT_PRESETS[emp.workShift]?.end   || '12:00',
    });
  };

  const handleShiftPresetChange = (preset: string) => {
    const p = SHIFT_PRESETS[preset];
    setShiftEdit({ workShift: preset, shiftStartTime: p?.start || '07:00', shiftEndTime: p?.end || '12:00' });
  };

  const handleSaveShift = async () => {
    if (!shiftModalEmployee) return;
    try {
      await apiService.updateEmployee(shiftModalEmployee.id, {
        workShift:      shiftEdit.workShift,
        shiftStartTime: shiftEdit.shiftStartTime,
        shiftEndTime:   shiftEdit.shiftEndTime,
      });
      setEmployees(prev => prev.map(e =>
        e.id === shiftModalEmployee.id
          ? { ...e, ...shiftEdit }
          : e
      ));
      setShiftModalEmployee(null);
      alert('✅ Đã cập nhật ca làm việc!');
    } catch (err: any) {
      alert(`❌ Lỗi: ${err.message}`);
    }
  };

  // Xóa nhân viên / Kick khỏi business
  const handleDeleteEmployee = async (emp: Employee): Promise<void> => {
    if (!hasPermission(PERMISSIONS.MANAGE_EMPLOYEES)) {
      alert(t('employees.messages.noPermissionDelete'));
      return;
    }
    const currentUserId = user?._id || user?.id;
    if (emp.id === currentUserId || emp.userId === currentUserId) {
      alert(t('employees.messages.cannotDeleteSelf'));
      return;
    }
    if (!window.confirm(t('employees.messages.confirmDeleteMember').replace('{name}', emp.name))) return;
    try {
      if (emp.isFromUserSystem && emp.userId) {
        await apiService.removeMember(emp.userId);
      } else {
        await apiService.deleteEmployee(emp.id);
      }
      setEmployees(prev => prev.filter(e => e.id !== emp.id));
      alert(t('employees.messages.deleteSuccess'));
    } catch (error: any) {
      alert(`${t('employees.messages.deleteErrorPrefix')}: ${error.message}`);
    }
  };

  // Thay đổi trạng thái nhân viên
  const handleToggleStatus = async (id: string, currentStatus: 'active' | 'inactive'): Promise<void> => {
    if (!hasPermission(PERMISSIONS.MANAGE_EMPLOYEES)) {
      alert(t('employees.messages.noPermissionStatus'));
      return;
    }

    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      const updatedEmployee = await apiService.changeEmployeeStatus(id, newStatus);

      // Cập nhật state
      setEmployees(prev =>
        prev.map(emp => emp.id === id ? { ...emp, status: newStatus as 'active' | 'inactive' } : emp)
      );

      alert(t(`employees.messages.statusChanged.${newStatus}`));
    } catch (error: any) {
      console.error('Error changing employee status:', error);
      alert(`${t('employees.messages.statusError')}: ${error.message}`);
    }
  };

  // Phân quyền cho nhân viên (Owner/Manager)
  const handleAssignRole = async (emp: Employee, newRole: string): Promise<void> => {
    if (user?.role !== ROLES.OWNER && user?.role !== ROLES.ADMIN) {
      alert(t('employees.messages.onlyOwnerCanAssignManager'));
      return;
    }
    try {
      if (emp.isFromUserSystem && emp.userId) {
        // Cập nhật qua User.businesses[] (nguồn chính)
        await apiService.updateMemberRole(emp.userId, newRole);
      } else {
        // Fallback: cập nhật Employee record
        await apiService.updateEmployee(emp.id, { role: newRole });
      }
      setEmployees(prev =>
        prev.map(e => e.id === emp.id ? { ...e, role: newRole } : e)
      );
      alert(`${t('employees.messages.roleUpdatedTo')}: ${newRole}`);
    } catch (error: any) {
      alert(`${t('employees.messages.deleteErrorPrefix')}: ${error.message}`);
    }
  };

  // Tìm kiếm nhân viên từ backend (server-side search)
  const handleSearch = async () => {
    if (searchTerm.trim()) {
      try {
        setLoading(true);
        const searchResults = await apiService.searchEmployees(searchTerm);
        // Nếu muốn chỉ hiển thị kết quả tìm kiếm, có thể set employees tạm thời
        // Hoặc có thể tạo state riêng cho kết quả tìm kiếm
        // Ở đây tôi sẽ để client-side filtering như cũ để giữ UI mượt mà
        console.log('Search results:', searchResults);
      } catch (error) {
        console.error('Error searching employees:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  // Hiển thị role
  const getRoleDisplay = (role: string): string => {
    const roleMap: Record<string, string> = {
      [ROLES.OWNER]: `👑 ${t('roles.owner')}`,
      [ROLES.MANAGER]: `📋 ${t('roles.manager')}`,
      [ROLES.STAFF]: `💇 ${t('roles.staff')}`,
      [ROLES.RECEPTIONIST]: `💁 ${t('roles.receptionist')}`,
      [ROLES.ADMIN]: `👨‍💼 ${t('roles.admin')}`,
      [ROLES.CUSTOMER]: `👤 ${t('roles.customer')}`
    };
    return roleMap[role] || role;
  };

  // Hiển thị ca làm việc
  const getShiftDisplay = (shift: string): string => {
    const shiftMap: Record<string, string> = {
      'Ca sáng': `☀️ ${t('shifts.morning')}`,
      'Ca chiều': `🌤️ ${t('shifts.afternoon')}`,
      'Ca tối': `🌙 ${t('shifts.evening')}`,
      'Ca đêm': `🌃 ${t('shifts.night')}`,
      'Toàn thời gian': `⏰ ${t('shifts.fulltime')}`,
      'Bán thời gian': `🕐 ${t('shifts.parttime')}`
    };
    return shiftMap[shift] || shift;
  };

  // Xử lý thay đổi khi chỉnh sửa
  const handleEditEmployeeChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    if (editingEmployee) {
      setEditingEmployee({
        ...editingEmployee,
        [name]: value
      });
    }
  };

  if (loading) {
    return (
      <div className="employees-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '500px' }}>
        <div className="loading-spinner" style={{ width: '44px', height: '44px', minWidth: '44px', minHeight: '44px', margin: '0 auto' }}></div>
        <p style={{ marginTop: '20px', color: '#667eea', fontWeight: '500' }}>{t('employees.loading')}</p>
      </div>
    );
  }

  return (
    <div className="employees-container">
      {/* Header */}
      <div className="employees-header">
        <div className="header-left">
          <h1>👥 {t('employees.title')}</h1>
          <p className="page-subtitle">
            {t('employees.stats.total')}: {employees.length} {t('dashboard.stats.employees').toLowerCase()} • {t('employees.stats.active')}: {employees.filter(e => e.status === 'active').length}
          </p>
        </div>
        <div className="header-actions">
          <button className="action-btn secondary" onClick={() => navigate('/dashboard')}>
            ← Dashboard
          </button>
          {/* Nhân viên chỉ tham gia qua Business ID — không cho thêm thủ công */}
        </div>
      </div>

      {/* Thống kê nhanh - Dùng dữ liệu từ backend nếu có */}
      <div className="quick-stats">
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>{t('employees.stats.total')}</h3>
            <p className="stat-value">{stats?.total || employees.length}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>{t('employees.stats.active')}</h3>
            <p className="stat-value">{stats?.active || employees.filter(e => e.status === 'active').length}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📋</div>
          <div className="stat-content">
            <h3>{t('employees.stats.manager')}</h3>
            <p className="stat-value">{stats?.byRole?.manager || employees.filter(e => e.role === ROLES.MANAGER).length}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⏰</div>
          <div className="stat-content">
            <h3>{t('employees.stats.morning')}</h3>
            <p className="stat-value">{stats?.byShift?.morning || employees.filter(e => e.workShift === 'Ca sáng').length}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('employees.searchPlaceholder')}
            value={searchTerm}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="search-input"
          />
          <button
            className="search-btn"
            onClick={handleSearch}
            style={{ marginLeft: '10px', padding: '10px 20px' }}
          >
            {t('employees.filters.searchBtn')}
          </button>
        </div>

        <div className="filter-controls">
          <div className="filter-group">
            <label>{t('employees.filters.role')}</label>
            <select
              className="filter-select"
              value={filterRole}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => setFilterRole(e.target.value)}
            >
              <option value="all">{t('employees.filters.allRoles')}</option>
              <option value={ROLES.OWNER}>{t('roles.owner')}</option>
              <option value={ROLES.MANAGER}>{t('roles.manager')}</option>
              <option value={ROLES.STAFF}>{t('roles.staff')}</option>
              <option value={ROLES.RECEPTIONIST}>{t('roles.receptionist')}</option>
              <option value={ROLES.ADMIN}>{t('roles.admin')}</option>
            </select>
          </div>

          <div className="filter-group">
            <label>{t('employees.filters.shift')}</label>
            <select
              className="filter-select"
              value={filterShift}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => setFilterShift(e.target.value)}
            >
              <option value="all">{t('employees.filters.allShifts')}</option>
              <option value="Ca sáng">{t('shifts.morning')}</option>
              <option value="Ca chiều">{t('shifts.afternoon')}</option>
              <option value="Ca tối">{t('shifts.evening')}</option>
              <option value="Ca đêm">{t('shifts.night')}</option>
              <option value="Toàn thời gian">{t('shifts.fulltime')}</option>
              <option value="Bán thời gian">{t('shifts.parttime')}</option>
            </select>
          </div>

          <div className="filter-group">
            <label>{t('employees.filters.status')}</label>
            <select
              className="filter-select"
              onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                const status = e.target.value;
                if (status === 'all') {
                  setFilterRole('all');
                }
              }}
            >
              <option value="all">{t('employees.filters.allStatuses')}</option>
              <option value="active">{t('employees.status.active')}</option>
              <option value="inactive">{t('employees.status.inactive')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Danh sách nhân viên */}
      <div className="employees-section">
        <div className="section-header">
          <h2>{t('employees.title')} ({filteredEmployees.length})</h2>
        </div>

        {filteredEmployees.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">👥</div>
            <h3>{t('employees.empty.title')}</h3>
            <p dangerouslySetInnerHTML={{ __html: t('employees.empty.hint1') }} />
            <p style={{fontSize:'0.85rem', color:'#888', marginTop:'8px'}}
               dangerouslySetInnerHTML={{ __html: t('employees.empty.hint2') }} />
          </div>
        ) : (
          <div className="employees-table-container">
            <table className="employees-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t('employees.table.name')}</th>
                  <th>Contact</th>
                  <th>{t('employees.table.position')}</th>
                  <th>Shift</th>
                  <th>{t('employees.table.status')}</th>
                  <th>{t('employees.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map((employee: Employee) => (
                  <tr key={employee.id} className={`employee-row ${employee.status}`}>
                    <td className="employee-id">
                      <span title={employee.userId || employee.id}>
                        #{String(employee.id).slice(-6).toUpperCase()}
                      </span>
                    </td>
                    <td className="employee-info">
                      <div className="employee-name">
                        {employee.name}
                        {(employee.userId === (user?._id || user?.id) ||
                          employee.id === (user?._id || user?.id)) &&
                          <span className="current-user-badge"> {t('employees.youBadge')}</span>}
                      </div>
                      {employee.email && <div style={{fontSize:'0.75rem', color:'#888'}}>{employee.email}</div>}
                    </td>
                    <td className="contact-info">
                      <div className="contact-phone">📱 {employee.phone || 'N/A'}</div>
                    </td>
                    <td>
                      <div className="role-control">
                        {(user?.role === ROLES.OWNER || user?.role === ROLES.ADMIN) &&
                          employee.userId !== (user?._id || user?.id) &&
                          employee.id !== (user?._id || user?.id) ? (
                          <select
                            className={`role-select ${employee.role}`}
                            value={employee.role}
                            onChange={(e: ChangeEvent<HTMLSelectElement>) => handleAssignRole(employee, e.target.value)}
                          >
                            <option value={ROLES.STAFF}>{t('roles.staff')}</option>
                            <option value={ROLES.RECEPTIONIST}>{t('roles.receptionist')}</option>
                            <option value={ROLES.MANAGER}>{t('roles.manager')}</option>
                            <option value={ROLES.OWNER}>{t('roles.owner')}</option>
                          </select>
                        ) : (
                          <span className={`role-badge ${employee.role}`}>
                            {getRoleDisplay(employee.role)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="work-shift">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span className={`shift-badge ${employee.workShift?.replace(' ', '-')}`}>
                          {getShiftDisplay(employee.workShift) || t('shifts.morning')}
                        </span>
                        {employee.shiftStartTime && employee.shiftEndTime && (
                          <span style={{ fontSize: '0.75rem', color: '#718096', whiteSpace: 'nowrap' }}>
                            {employee.shiftStartTime}–{employee.shiftEndTime}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="status-control">
                        <span className={`status-display ${employee.status}`}>
                          {employee.status === 'active' ? `✅ ${t('employees.status.active')}` : `⏸️ ${t('employees.status.inactive')}`}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="action-btn view"
                          onClick={() => {
                            alert(
                              `${t('employees.detailAlert.title')}\n` +
                              `${t('employees.detailAlert.name')}: ${employee.name}\n` +
                              `${t('employees.detailAlert.email')}: ${employee.email || 'N/A'}\n` +
                              `${t('employees.detailAlert.phone')}: ${employee.phone || 'N/A'}\n` +
                              `${t('employees.detailAlert.role')}: ${getRoleDisplay(employee.role)}\n` +
                              `${t('employees.detailAlert.shift')}: ${getShiftDisplay(employee.workShift) || t('shifts.morning')}\n` +
                              `${t('employees.detailAlert.joinedAt')}: ${employee.joinedAt ? new Date(employee.joinedAt).toLocaleDateString() : 'N/A'}`
                            );
                          }}
                          title={t('employees.detailAlert.title')}
                        >
                          👁
                        </button>

                        {/* Ca làm việc */}
                        {hasPermission(PERMISSIONS.MANAGE_EMPLOYEES) && (
                          <button
                            className="action-btn"
                            onClick={() => openShiftModal(employee)}
                            title="Chỉnh ca làm việc"
                            style={{ background: 'linear-gradient(135deg,#667eea,#764ba2)', color:'#fff', border:'none' }}
                          >
                            ⏰
                          </button>
                        )}

                        {hasPermission(PERMISSIONS.MANAGE_EMPLOYEES) &&
                          employee.userId !== (user?._id || user?.id) &&
                          employee.id !== (user?._id || user?.id) && (
                          <button
                            className="action-btn delete"
                            onClick={() => handleDeleteEmployee(employee)}
                            title="Xóa khỏi doanh nghiệp"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal chỉnh sửa (ca làm việc) */}
      {editingEmployee && hasPermission(PERMISSIONS.MANAGE_EMPLOYEES) && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>✎ {t('employees.modals.editTitle')}</h3>
              <button
                className="modal-close"
                onClick={() => setEditingEmployee(null)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="modal-form-group">
                <label>{t('employees.modals.labels.name')} *</label>
                <input
                  type="text"
                  name="name"
                  placeholder={t('dashboard.modals.placeholders.customerName').replace('khách hàng', 'nhân viên')}
                  value={editingEmployee.name}
                  onChange={handleEditEmployeeChange}
                  className="modal-input"
                  required
                />
              </div>

              <div className="modal-form-group">
                <label>{t('employees.modals.labels.phone')} *</label>
                <input
                  type="tel"
                  name="phone"
                  placeholder={t('dashboard.modals.placeholders.phone')}
                  value={editingEmployee.phone}
                  onChange={handleEditEmployeeChange}
                  className="modal-input"
                  required
                />
              </div>

              <div className="modal-form-group">
                <label>{t('employees.modals.labels.role')} *</label>
                <select
                  name="role"
                  value={editingEmployee.role}
                  onChange={handleEditEmployeeChange}
                  className="modal-select"
                  disabled={editingEmployee.id === user?.id}
                  required
                >
                  {user?.role === ROLES.OWNER || user?.role === ROLES.ADMIN ? (
                    <>
                      <option value={ROLES.STAFF}>{t('employees.modals.staffOption')}</option>
                      <option value={ROLES.RECEPTIONIST}>{t('roles.receptionist')}</option>
                      <option value={ROLES.MANAGER}>{t('employees.modals.managerOption')}</option>
                    </>
                  ) : (
                    <option value={ROLES.STAFF}>{t('employees.modals.staffOption')}</option>
                  )}
                </select>
              </div>

              <div className="modal-form-group">
                <label>{t('employees.modals.shiftLabel')}</label>
                <select
                  name="workShift"
                  value={editingEmployee.workShift}
                  onChange={handleEditEmployeeChange}
                  className="modal-select"
                  required
                >
                  <option value="Ca sáng">{t('shifts.morning')}</option>
                  <option value="Ca chiều">{t('shifts.afternoon')}</option>
                  <option value="Ca tối">{t('shifts.evening')}</option>
                  <option value="Ca đêm">{t('shifts.night')}</option>
                  <option value="Toàn thời gian">{t('shifts.fulltime')}</option>
                  <option value="Bán thời gian">{t('shifts.parttime')}</option>
                </select>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="modal-btn cancel"
                onClick={() => setEditingEmployee(null)}
              >
                {t('employees.modals.cancel')}
              </button>
              <button
                className="modal-btn submit"
                onClick={handleUpdateEmployee}
              >
                {t('employees.modals.update')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Shift Edit Modal ── */}
      {shiftModalEmployee && (
        <div className="modal-overlay" onClick={() => setShiftModalEmployee(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg,#667eea,#764ba2)' }}>
              <h3 style={{ color: '#fff', margin: 0 }}>⏰ {t('employees.modals.shiftEditTitle')}{shiftModalEmployee.name}</h3>
              <button className="modal-close" style={{ color: '#fff', background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer' }} onClick={() => setShiftModalEmployee(null)}>×</button>
            </div>
            <div className="modal-body">
              {/* Preset selector */}
              <div className="modal-form-group">
                <label style={{ fontWeight: 600 }}>{t('employees.modals.shiftType')}</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px', marginTop: '8px' }}>
                  {Object.keys(SHIFT_PRESETS).map(preset => (
                    <button
                      key={preset}
                      onClick={() => handleShiftPresetChange(preset)}
                      style={{
                        padding: '8px 4px', borderRadius: '8px', border: '2px solid',
                        borderColor: shiftEdit.workShift === preset ? '#667eea' : '#e2e8f0',
                        background: shiftEdit.workShift === preset ? 'linear-gradient(135deg,#667eea,#764ba2)' : '#f7fafc',
                        color: shiftEdit.workShift === preset ? '#fff' : '#4a5568',
                        fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s'
                      }}
                    >
                      {preset === 'Ca sáng' ? '☀️' : preset === 'Ca chiều' ? '🌤' :
                       preset === 'Ca tối' ? '🌙' : preset === 'Ca đêm' ? '🌃' :
                       preset === 'Toàn thời gian' ? '⏰' : '🕐'} 
                       {preset === 'Ca sáng' ? (t('employees.shifts.morning') || 'Ca sáng') : 
                        preset === 'Ca chiều' ? (t('employees.shifts.afternoon') || 'Ca chiều') : 
                        preset === 'Ca tối' ? (t('employees.shifts.evening') || 'Ca tối') : 
                        preset === 'Ca đêm' ? (t('employees.shifts.night') || 'Ca đêm') : 
                        preset === 'Toàn thời gian' ? (t('employees.shifts.fullTime') || 'Toàn thời gian') : 
                        preset === 'Bán thời gian' ? (t('employees.shifts.partTime') || 'Bán thời gian') : preset}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom time inputs */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '16px' }}>
                <div className="modal-form-group">
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: '6px' }}>🕐 {t('employees.modals.shiftStartTime')}</label>
                  <input
                    type="time"
                    value={shiftEdit.shiftStartTime}
                    onChange={e => setShiftEdit(prev => ({ ...prev, shiftStartTime: e.target.value }))}
                    className="modal-input"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '1rem' }}
                  />
                </div>
                <div className="modal-form-group">
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: '6px' }}>🕐 {t('employees.modals.shiftEndTime')}</label>
                  <input
                    type="time"
                    value={shiftEdit.shiftEndTime}
                    onChange={e => setShiftEdit(prev => ({ ...prev, shiftEndTime: e.target.value }))}
                    className="modal-input"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '1rem' }}
                  />
                </div>
              </div>

              {/* Preview */}
              <div style={{
                marginTop: '16px', padding: '12px 16px',
                background: 'linear-gradient(135deg,rgba(102,126,234,0.08),rgba(118,75,162,0.08))',
                borderRadius: '10px', border: '1px solid rgba(102,126,234,0.2)',
                textAlign: 'center'
              }}>
                <div style={{ fontWeight: 700, color: '#4a5568', fontSize: '0.9rem' }}>{t('employees.modals.shiftPreview')}</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#553c9a', marginTop: '6px' }}>
                  {shiftEdit.workShift === 'Ca sáng' ? (t('employees.shifts.morning') || 'Ca sáng') : 
                   shiftEdit.workShift === 'Ca chiều' ? (t('employees.shifts.afternoon') || 'Ca chiều') : 
                   shiftEdit.workShift === 'Ca tối' ? (t('employees.shifts.evening') || 'Ca tối') : 
                   shiftEdit.workShift === 'Ca đêm' ? (t('employees.shifts.night') || 'Ca đêm') : 
                   shiftEdit.workShift === 'Toàn thời gian' ? (t('employees.shifts.fullTime') || 'Toàn thời gian') : 
                   shiftEdit.workShift === 'Bán thời gian' ? (t('employees.shifts.partTime') || 'Bán thời gian') : shiftEdit.workShift} 
                   · {shiftEdit.shiftStartTime} – {shiftEdit.shiftEndTime}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn cancel" onClick={() => setShiftModalEmployee(null)}>{t('employees.modals.cancel')}</button>
              <button className="modal-btn submit" onClick={handleSaveShift}
                style={{ background: 'linear-gradient(135deg,#667eea,#764ba2)' }}
              >
                💾 {t('employees.modals.saveShift')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permissions Info Section */}
      <div className="permissions-info">
        <h3>📋 {t('employees.permissions.title')}</h3>
        <div className="permissions-grid">
          <div className="permission-item">
            <div className="permission-icon">👑</div>
            <div className="permission-content">
              <h4>{t('employees.permissions.owner.title')}</h4>
              <ul>
                <li>{t('employees.permissions.owner.perm1')}</li>
                <li>{t('employees.permissions.owner.perm2')}</li>
                <li>{t('employees.permissions.owner.perm3')}</li>
                <li>{t('employees.permissions.owner.perm4')}</li>
              </ul>
            </div>
          </div>
          <div className="permission-item">
            <div className="permission-icon">📋</div>
            <div className="permission-content">
              <h4>{t('employees.permissions.manager.title')}</h4>
              <ul>
                <li>{t('employees.permissions.manager.perm1')}</li>
                <li>{t('employees.permissions.manager.perm2')}</li>
                <li>{t('employees.permissions.manager.perm3')}</li>
                <li>{t('employees.permissions.manager.perm4')}</li>
              </ul>
            </div>
          </div>
          <div className="permission-item">
            <div className="permission-icon">💇</div>
            <div className="permission-content">
              <h4>{t('employees.permissions.staff.title')}</h4>
              <ul>
                <li>{t('employees.permissions.staff.perm1')}</li>
                <li>{t('employees.permissions.staff.perm2')}</li>
                <li>{t('employees.permissions.staff.perm3')}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Employees;